import { resume, resumeAnchor } from "./resume";

// OrbStack's Docker containers, volumes and images, from Sylvester's Mac as his screenshots of it
// show them (with some project names changed), and each one's details from Docker itself (bind
// mounts left out, as they point into his Mac). `created` dates are as of when the screenshots
// were taken (TAKEN); the window moves them along to the visit, so ages always read the same.
//
// Images: `size` in MB as OrbStack shows it; `look` is one of OrbStack's logos, or the color of the
// box it draws for others. Containers: by Compose `project` and service `name` (or just a name),
// `running` as in the screenshot; they say which images and volumes are in use.

export const TAKEN = Date.parse("2026-10-26T11:00:00Z");

// OrbStack's colors for icons it draws itself.
export const PALETTE = {
  gray: "#a5a5a8",
  indigo: "#8177f8",
  magenta: "#d85ee7",
  cyan: "#58d0d9",
  blue: "#4ca0ff",
  tan: "#bd997f",
  teal: "#59d4c2",
  green: "#69d27d",
};

// Compose projects' colors.
export const PROJECTS = {
  rhodes: "indigo",
  atlas: "gray",
  kite: "gray",
  "node-nest-tasks-dev": "cyan",
  meridian: "teal",
  "oauth2-proxy": "blue",
  lantern: "indigo",
  "test-fineract": "green",
};

// Where an image shows up in the resume.
const ANCHORS = { skills: resumeAnchor("Technical skills"), optiswift: resumeAnchor(resume.experience[0].company) };

const IMAGE_LIST = [
  {
    ref: "rhodes-api:latest",
    id: "0e967ca4d396",
    created: "2026-10-22T08:31:30Z",
    size: 659.2,
    look: "gray",
    platform: "linux/arm64",
    command: null,
    entrypoint: "/usr/local/bin/server",
    workdir: null,
    stopSignal: null,
    env: [
      ["GPG_KEY", "7169605F62C751356D054A26A821E680E5FA6305"],
      ["PATH", "/usr/local/bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["PYTHON_SHA256", "f4b1bfb3c79b5bb11b8d228a12504163b4c0dab4d679828d8f5f26b6cb6ab35d"],
      ["PYTHON_VERSION", "3.13.16"],
    ],
    volumes: [],
    ports: ["8080/tcp"],
  },
  {
    ref: "rhodes-web:latest",
    id: "c1aa894bd298",
    created: "2026-10-21T21:35:29Z",
    size: 543.3,
    look: "gray",
    platform: "linux/arm64",
    command: "sh -c rm -f .astro/dev.json && exec npm run dev -- --host 0.0.0.0",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/app",
    stopSignal: null,
    env: [
      ["NODE_VERSION", "22.23.3"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["YARN_VERSION", "1.22.22"],
    ],
    volumes: [],
    ports: ["4321/tcp"],
  },
  {
    ref: "node-nest-tasks-dev-api:latest",
    id: "23da6bafe89c",
    created: "2026-10-06T23:24:29Z",
    size: 676.7,
    look: "node",
    platform: "linux/arm64",
    command: "node",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/workspace",
    stopSignal: null,
    env: [
      ["NODE_VERSION", "24.15.0"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["YARN_VERSION", "1.22.22"],
    ],
    volumes: [],
    ports: [],
  },
  {
    ref: "node-nest-tasks-dev-web:latest",
    id: "324187018f57",
    created: "2026-10-06T23:24:29Z",
    size: 676.7,
    look: "node",
    platform: "linux/arm64",
    command: "node",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/workspace",
    stopSignal: null,
    env: [
      ["NODE_VERSION", "24.15.0"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["YARN_VERSION", "1.22.22"],
    ],
    volumes: [],
    ports: [],
  },
  {
    ref: "node-nest-tasks-dev-seed:latest",
    id: "73ac58bbf8a3",
    created: "2026-10-06T23:17:32Z",
    size: 676.7,
    look: "node",
    platform: "linux/arm64",
    command: "node",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/workspace",
    stopSignal: null,
    env: [
      ["NODE_VERSION", "24.15.0"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["YARN_VERSION", "1.22.22"],
    ],
    volumes: [],
    ports: [],
  },
  {
    ref: "redis:8-alpine",
    id: "675f7644831d",
    created: "2026-09-21T17:37:05Z",
    size: 118.6,
    look: "redis",
    platform: "linux/arm64/v8",
    command: "redis-server",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/data",
    stopSignal: null,
    env: [
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["REDIS_VERSION", "8.10.2"],
    ],
    volumes: [],
    ports: ["6379/tcp"],
    resume: ["Redis is one of the databases I work with.", "skills"],
  },
  {
    ref: "postgres:18-alpine",
    id: "d7a8005067f5",
    created: "2026-09-17T21:30:59Z",
    size: 298,
    look: "postgres",
    platform: "linux/arm64/v8",
    command: "postgres",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/",
    stopSignal: "SIGINT",
    env: [
      ["DOCKER_PG_LLVM_DEPS", "llvm21-dev \t\tclang21"],
      ["GOSU_VERSION", "1.19"],
      ["LANG", "en_US.utf8"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["PGDATA", "/var/lib/postgresql/18/docker"],
      ["PG_MAJOR", "18"],
      ["PG_SHA256", "555610c24d53e4316da5b7d3fc25c279d96856d5e0e23ee308c328c5fa881d9f"],
      ["PG_VERSION", "18.6"],
    ],
    volumes: ["/var/lib/postgresql"],
    ports: ["5432/tcp"],
    resume: ["PostgreSQL is one of the databases I work with.", "skills"],
  },
  {
    ref: "apache/fineract:latest",
    id: "ade70b44fcaf",
    created: "2026-06-06T20:57:54Z",
    size: 570.8,
    look: "apache",
    platform: "linux/arm64",
    command: "-Duser.home=/tmp -Dfile.encoding=UTF-8 -Duser.timezone=UTC -Djava.security.egd=file:/dev/./urandom",
    entrypoint: "java -cp @/app/jib-classpath-file org.apache.fineract.ServerApplication",
    workdir: "/",
    stopSignal: null,
    env: [
      ["JAVA_HOME", "/usr/lib/jvm/zulu21"],
      ["LANG", "en_US.UTF-8"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
    ],
    volumes: [],
    ports: ["8080/tcp", "8443/tcp"],
    resume: ["Apache Fineract, the core banking system I integrated at Optiswift.", "optiswift"],
  },
  {
    ref: "openmf/web-app:dev",
    id: "9f452efe032f",
    created: "2026-06-06T16:40:27Z",
    size: 48.9,
    look: "green",
    platform: "linux/arm64",
    command:
      "/bin/sh -c envsubst < /usr/share/nginx/html/assets/env.template.js > /usr/share/nginx/html/assets/env.js && exec nginx -g 'daemon off;'",
    entrypoint: "/docker-entrypoint.sh",
    workdir: "/",
    stopSignal: "SIGQUIT",
    env: [
      ["DYNPKG_RELEASE", "1"],
      ["NGINX_VERSION", "1.31.1"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["PKG_RELEASE", "1"],
    ],
    volumes: [],
    ports: ["80/tcp"],
    resume: ["The Mifos web app, from moving Optiswift's core banking to Mifos.", "optiswift"],
  },
  {
    ref: "postgres:16-alpine",
    id: "fedf56a0b46d",
    created: "2026-04-15T20:22:35Z",
    size: 271.6,
    look: "postgres",
    platform: "linux/arm64/v8",
    command: "postgres",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/",
    stopSignal: "SIGINT",
    env: [
      ["DOCKER_PG_LLVM_DEPS", "llvm19-dev \t\tclang19"],
      ["GOSU_VERSION", "1.19"],
      ["LANG", "en_US.utf8"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["PGDATA", "/var/lib/postgresql/data"],
      ["PG_MAJOR", "16"],
      ["PG_SHA256", "dc2ddbbd245c0265a689408e3d2f2f3f9ba2da96bd19318214b313cdd9797287"],
      ["PG_VERSION", "16.13"],
    ],
    volumes: ["/var/lib/postgresql/data"],
    ports: ["5432/tcp"],
  },
  {
    ref: "postgres:17.8-bookworm",
    id: "e02c2a7cd9f7",
    created: "2026-02-24T19:21:40Z",
    size: 458.6,
    look: "postgres",
    platform: "linux/arm64/v8",
    command: "postgres",
    entrypoint: "docker-entrypoint.sh",
    workdir: null,
    stopSignal: "SIGINT",
    env: [
      ["GOSU_VERSION", "1.19"],
      ["LANG", "en_US.utf8"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin:/usr/lib/postgresql/17/bin"],
      ["PGDATA", "/var/lib/postgresql/data"],
      ["PG_MAJOR", "17"],
      ["PG_VERSION", "17.8-1.pgdg12+1"],
    ],
    volumes: ["/var/lib/postgresql/data"],
    ports: ["5432/tcp"],
  },
  {
    ref: "oauth2-proxy-backend:latest",
    id: "e46720a3078a",
    created: "2025-11-18T08:44:04Z",
    size: 15.8,
    look: "tan",
    platform: "linux/arm64",
    command: "./main",
    entrypoint: null,
    workdir: "/root/",
    stopSignal: null,
    env: [["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"]],
    volumes: [],
    ports: ["8080/tcp"],
  },
  {
    ref: "postgres:latest",
    id: "b246b1ef15b7",
    created: "2025-11-18T02:59:43Z",
    size: 478.9,
    look: "postgres",
    platform: "linux/arm64/v8",
    command: "postgres",
    entrypoint: "docker-entrypoint.sh",
    workdir: null,
    stopSignal: "SIGINT",
    env: [
      ["GOSU_VERSION", "1.19"],
      ["LANG", "en_US.utf8"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin:/usr/lib/postgresql/18/bin"],
      ["PGDATA", "/var/lib/postgresql/18/docker"],
      ["PG_MAJOR", "18"],
      ["PG_VERSION", "18.1-1.pgdg13+2"],
    ],
    volumes: ["/var/lib/postgresql"],
    ports: ["5432/tcp"],
  },
  {
    ref: "lantern:latest",
    id: "3fccf00d388e",
    created: "2025-11-05T10:45:34Z",
    size: 492.3,
    look: "cyan",
    platform: "linux/arm64",
    command: null,
    entrypoint: "./app",
    workdir: "/app",
    stopSignal: null,
    env: [
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["PRISMA_GLOBAL_CACHE_DIR", "/app/prisma/cache"],
    ],
    volumes: [],
    ports: ["8080/tcp"],
  },
  {
    ref: "postgres:16",
    id: "45905af0e6f0",
    created: "2025-10-15T18:23:03Z",
    size: 473.6,
    look: "postgres",
    platform: "linux/arm64/v8",
    command: "postgres",
    entrypoint: "docker-entrypoint.sh",
    workdir: null,
    stopSignal: "SIGINT",
    env: [
      ["GOSU_VERSION", "1.19"],
      ["LANG", "en_US.utf8"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin:/usr/lib/postgresql/16/bin"],
      ["PGDATA", "/var/lib/postgresql/data"],
      ["PG_MAJOR", "16"],
      ["PG_VERSION", "16.10-1.pgdg13+1"],
    ],
    volumes: ["/var/lib/postgresql/data"],
    ports: ["5432/tcp"],
  },
  {
    ref: "migrate/migrate:v4.18.1",
    id: "a47069f811b6",
    created: "2024-09-08T22:41:05Z",
    size: 60.1,
    look: "tan",
    platform: "linux/arm64",
    command: "--help",
    entrypoint: "migrate",
    workdir: null,
    stopSignal: null,
    env: [["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"]],
    volumes: [],
    ports: [],
  },
  {
    ref: "quay.io/oauth2-proxy/oauth2-proxy:v7.6.0",
    id: "d16e6edbfc35",
    created: "2024-02-13T11:38:10Z",
    size: 33.4,
    look: "cyan",
    platform: "linux/arm64",
    command: null,
    entrypoint: "/bin/oauth2-proxy",
    workdir: "/home/nonroot",
    stopSignal: null,
    env: [
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["SSL_CERT_FILE", "/etc/ssl/certs/ca-certificates.crt"],
    ],
    volumes: [],
    ports: [],
  },
  {
    ref: "node-nest-tasks-verify-pr2-verify:latest",
    id: "24a6abd5c277",
    created: "2026-10-06T23:52:59Z",
    size: 677.1,
    look: "node",
    platform: "linux/arm64",
    command: "node",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/workspace",
    stopSignal: null,
    env: [
      ["NODE_VERSION", "24.15.0"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["YARN_VERSION", "1.22.22"],
    ],
    volumes: [],
    ports: [],
  },
  {
    ref: "node-nest-tasks-test-pr1-test:latest",
    id: "d221d13568c0",
    created: "2026-10-06T23:52:59Z",
    size: 677.1,
    look: "node",
    platform: "linux/arm64",
    command: "node",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/workspace",
    stopSignal: null,
    env: [
      ["NODE_VERSION", "24.15.0"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["YARN_VERSION", "1.22.22"],
    ],
    volumes: [],
    ports: [],
  },
  {
    ref: "node-nest-tasks-test-pr2-test:latest",
    id: "29103f09b548",
    created: "2026-10-06T23:52:59Z",
    size: 677.1,
    look: "node",
    platform: "linux/arm64",
    command: "node",
    entrypoint: "docker-entrypoint.sh",
    workdir: "/workspace",
    stopSignal: null,
    env: [
      ["NODE_VERSION", "24.15.0"],
      ["PATH", "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin"],
      ["YARN_VERSION", "1.22.22"],
    ],
    volumes: [],
    ports: [],
  },
];

export const CONTAINERS = [
  {
    id: "b1f0586f01e1",
    name: "api",
    project: "rhodes",
    image: "rhodes-api",
    created: "2026-10-22T08:31:30Z",
    running: true,
    ports: ["8081:8080/tcp"],
    mounts: [],
  },
  {
    id: "7a5462cc0455",
    name: "web",
    project: "rhodes",
    image: "rhodes-web",
    created: "2026-10-21T21:35:41Z",
    running: true,
    ports: ["4321:4321/tcp"],
    mounts: [["558821a16ff4f6200a7d90552fa75aaf39ba1683276c5a31e7705d69f517301f", "/app/node_modules"]],
  },
  {
    id: "e4508a9ec1ba",
    name: "postgres",
    project: "rhodes",
    image: "postgres:18-alpine",
    created: "2026-10-06T18:38:38Z",
    running: true,
    ports: ["5433:5432/tcp"],
    mounts: [["rhodes_pgdata", "/var/lib/postgresql"]],
  },
  {
    id: "53fbb6f94717",
    name: "redis",
    project: "rhodes",
    image: "redis:8-alpine",
    created: "2026-10-06T18:38:38Z",
    running: true,
    ports: ["6379:6379/tcp"],
    mounts: [],
  },
  {
    id: "5ae58a4b0832",
    name: "postgres",
    project: "atlas",
    image: "postgres:16",
    created: "2025-11-20T18:30:53Z",
    running: false,
    ports: ["5432:5432/tcp"],
    mounts: [["atlas_postgres", "/var/lib/postgresql/data"]],
  },
  {
    id: "3738cbe2a113",
    name: "backend",
    project: "kite",
    image: "kite-backend",
    created: "2026-04-24T15:24:31Z",
    running: false,
    ports: ["8081:8080/tcp"],
    mounts: [],
  },
  {
    id: "90e2a8af85a9",
    name: "frontend",
    project: "kite",
    image: "kite-frontend",
    created: "2026-04-24T15:20:40Z",
    running: false,
    ports: ["3000:80/tcp"],
    mounts: [],
  },
  {
    id: "53800a568bfa",
    name: "migrate",
    project: "kite",
    image: "migrate/migrate:v4.18.1",
    created: "2026-04-21T21:05:41Z",
    running: false,
    ports: [],
    mounts: [],
  },
  {
    id: "62439944b496",
    name: "postgres",
    project: "kite",
    image: "postgres:16-alpine",
    created: "2026-04-21T21:05:41Z",
    running: false,
    ports: ["5433:5432/tcp"],
    mounts: [["kite_kite_pgdata", "/var/lib/postgresql/data"]],
  },
  {
    id: "56e596942988",
    name: "web",
    project: "node-nest-tasks-dev",
    image: "node-nest-tasks-dev-web",
    created: "2026-10-06T23:24:40Z",
    running: false,
    ports: ["5174:5173/tcp"],
    mounts: [["node-nest-tasks-dev_node-modules", "/workspace/node_modules"]],
  },
  {
    id: "f92a0e953fff",
    name: "api",
    project: "node-nest-tasks-dev",
    image: "node-nest-tasks-dev-api",
    created: "2026-10-06T23:24:30Z",
    running: false,
    ports: ["3100:3000/tcp"],
    mounts: [["node-nest-tasks-dev_node-modules", "/workspace/node_modules"]],
  },
  {
    id: "31d0f4cb5f1f",
    name: "seed",
    project: "node-nest-tasks-dev",
    image: "node-nest-tasks-dev-seed",
    created: "2026-10-06T23:17:44Z",
    running: false,
    ports: [],
    mounts: [],
  },
  {
    id: "9967a95df31f",
    name: "postgres-dev",
    project: "node-nest-tasks-dev",
    image: "postgres:17.8-bookworm",
    created: "2026-10-06T23:17:32Z",
    running: false,
    ports: [],
    mounts: [["node-nest-tasks-dev_postgres-dev", "/var/lib/postgresql/data"]],
  },
  {
    id: "7fece9828799",
    name: "app",
    project: "meridian",
    image: "meridian",
    created: "2026-07-09T08:56:27Z",
    running: false,
    ports: ["8000:8000/tcp"],
    mounts: [],
  },
  {
    id: "b1ebf9532921",
    name: "migration",
    project: "meridian",
    image: "meridian",
    created: "2026-07-09T08:56:27Z",
    running: false,
    ports: [],
    mounts: [],
  },
  {
    id: "735100ab1689",
    name: "postgres",
    project: "meridian",
    image: "postgres:16",
    created: "2026-07-09T08:56:27Z",
    running: false,
    ports: ["5432:5432/tcp"],
    mounts: [["meridian_postgres", "/var/lib/postgresql/data"]],
  },
  {
    id: "53464bda289b",
    name: "oauth2-proxy",
    project: "oauth2-proxy",
    image: "quay.io/oauth2-proxy/oauth2-proxy:v7.6.0",
    created: "2025-11-18T08:52:14Z",
    running: false,
    ports: ["4180:4180/tcp"],
    mounts: [],
  },
  {
    id: "ae9e2f1bfa49",
    name: "backend",
    project: "oauth2-proxy",
    image: "oauth2-proxy-backend",
    created: "2025-11-18T08:46:12Z",
    running: false,
    ports: ["7007:7007/tcp"],
    mounts: [],
  },
  {
    id: "97bd2a1ddf22",
    name: "app",
    project: "lantern",
    image: "lantern",
    created: "2025-11-06T11:43:36Z",
    running: false,
    ports: ["8080:8080/tcp"],
    mounts: [],
  },
  {
    id: "1cdfad736bf8",
    name: "migration",
    project: "lantern",
    image: "lantern",
    created: "2025-11-06T11:43:36Z",
    running: false,
    ports: [],
    mounts: [],
  },
  {
    id: "4dfc1858b556",
    name: "postgres",
    project: "lantern",
    image: "postgres:16",
    created: "2025-11-06T11:43:36Z",
    running: false,
    ports: ["5400:5432/tcp"],
    mounts: [["lantern_postgres", "/var/lib/postgresql/data"]],
  },
  {
    id: "3d50e97f6a53",
    name: "web-app",
    project: "test-fineract",
    image: "openmf/web-app:dev",
    created: "2026-06-08T10:47:08Z",
    running: false,
    ports: ["9090:80/tcp"],
    mounts: [],
  },
  {
    id: "13d9a3eb186c",
    name: "fineract",
    project: "test-fineract",
    image: "apache/fineract:latest",
    created: "2026-06-08T10:47:08Z",
    running: false,
    ports: ["8444:8080/tcp"],
    mounts: [],
  },
  {
    id: "973d1c27a8ef",
    name: "postgres",
    project: "test-fineract",
    image: "postgres:16",
    created: "2026-06-08T10:47:06Z",
    running: false,
    ports: ["5434:5432/tcp"],
    mounts: [["test-fineract_test_postgres", "/var/lib/postgresql/data"]],
  },
  {
    id: "923304e3d2e1",
    name: "cabin-deputy",
    project: null,
    image: "postgres",
    created: "2026-04-22T19:42:38Z",
    running: false,
    ports: ["5438:5432/tcp"],
    mounts: [["041c31eea67fc68165448a677967d4b8be8e84a518ca5b8c19f36d02a198dfa9", "/var/lib/postgresql"]],
  },
  {
    id: "15961998e1d5",
    name: "pouchie",
    project: null,
    image: "postgres:16",
    created: "2025-11-02T15:32:52Z",
    running: false,
    ports: ["5801:5432/tcp"],
    mounts: [["1c78634db515f0d8fbe92876ea594ee5b18721b5a8635002d3a96c2b8a2afe54", "/var/lib/postgresql/data"]],
  },
  {
    id: "423bd3ca9853",
    name: "lantern-7-db",
    project: null,
    image: "postgres",
    created: "2026-05-22T13:02:40Z",
    running: false,
    ports: ["5432:5434/tcp"],
    mounts: [["16d43e6be5cb45e194eec0d3f3f96a8f439352af6137dc2767b0e94a64d9434d", "/var/lib/postgresql"]],
  },
];

export const VOLUMES = [
  { name: "041c31eea67fc68165448a677967d4b8be8e84a518ca5b8c19f36d02a198dfa9", size: 66.2, look: "indigo" },
  { name: "16d43e6be5cb45e194eec0d3f3f96a8f439352af6137dc2767b0e94a64d9434d", size: 0, look: "gray" },
  { name: "1c78634db515f0d8fbe92876ea594ee5b18721b5a8635002d3a96c2b8a2afe54", size: 66.7, look: "magenta" },
  { name: "558821a16ff4f6200a7d90552fa75aaf39ba1683276c5a31e7705d69f517301f", size: 338.9, look: "cyan" },
  { name: "atlas_postgres", size: 173, look: "gray" },
  { name: "kite_kite_pgdata", size: 48.7, look: "indigo" },
  { name: "node-nest-tasks-dev_node-modules", size: 403.8, look: "indigo" },
  { name: "node-nest-tasks-dev_postgres-dev", size: 48.9, look: "magenta" },
  { name: "meridian_postgres", size: 0, look: "blue" },
  { name: "rhodes_pgdata", size: 65.3, look: "tan" },
  { name: "lantern_postgres", size: 48.9, look: "tan" },
  { name: "test-fineract_test_postgres", size: 110.1, look: "teal" },
  { name: "10af0ffbc94461fdf4543fee55bf03a8cf1b9313e06fd8aa7d44bd978cf420ba", size: 291.2, look: "tan" },
  { name: "1356bd2dcd81e893c758a96fe652586e953fec4877855c4dbb1c82f0346a6ada", size: 0, look: "green" },
  { name: "142e3b2e6458e54c93e366ccebe439dab03a95221e7a6e86d715f511631eb9ca", size: 0, look: "magenta" },
  { name: "3331d4319b75a07d4a7913ae0748868f59a570602570ac3e021ce7768c589b98", size: 320.6, look: "indigo" },
  { name: "34aee7f3d787990deac8099ab8c50cbacffba30e2c1e5e1f7eaa00b81def498d", size: 294, look: "gray" },
  { name: "34f6e0b38ef6dc2a3fad8c02fff876e0dbb741113b4f53f94eb44a72836c02b1", size: 17, look: "tan" },
  { name: "6c52c2090648f8d4c25bba5cfe93b5a7184be5be498c2ac5edecd46aae62b76d", size: 290, look: "tan" },
  { name: "705fef7f2c6edbcad14a7a3ccf9b6b496018ef538348660e96f97806219b2f05", size: 256.7, look: "green" },
];

export const IMAGES = IMAGE_LIST.map(({ resume: link, ...image }) =>
  link ? { ...image, resume: { text: link[0], anchor: ANCHORS[link[1]] } } : image,
);
